// hooks/useProfile.ts
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";


export interface Profile {
  id: string;
  remaining_attempts: number;
  has_paid: boolean;
}

const PROFILE_FIELDS = 'id, remaining_attempts, has_paid';

export function useProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile, isLoading, error } = useQuery<Profile, Error>({
    queryKey: ['profile'],
    queryFn: async () => {
      if (!user) throw new Error('No authenticated user');

      const { data, error: profileError } = await supabase
        .from('profiles')
        .select(PROFILE_FIELDS)
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!data) throw new Error('No profile data found');

      // Explicitly type the return value
      return data as Profile;
    }
  });

  const updateProfile = useMutation<void, Error, Partial<Profile>>({
    mutationFn: async (updates) => {
      if (!user) throw new Error('No authenticated user');

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.setQueryData<Profile>(['profile'], (old) => {
        if (!old) return old;
        return { ...old, ...variables };
      });
      toast.success("Profile updated successfully");
    },
    onError: (error) => {
      console.error('Failed to update profile:', error);
      toast.error("Failed to update profile");
    },
  });

  return {
    profile,
    isLoading,
    error,
    updateProfile
  };
}